/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
    hooks as commonHooks,
    types as commonTypes,
    analytics as commonAnalytics,
} from '@sitebuilder/common';

import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import { defaultStyles, themeStyles } from './FavoriteButton.styles';
import FavoriteButton, { FavoriteButtonProps } from './FavoriteButton';

export interface WidgetProperties extends commonTypes.WidgetPropertiesBase, FavoriteButtonProps {}

const FavoriteButtonWrapper = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const i18n = commonHooks.useLocalization();
    const tracking = commonAnalytics.useTracking();

    if (!widgetProperties) {
        return null;
    }

    const {
        styles,
        storageName,
        shouldDisplayTextIconOrBoth,
        itemToAddOrRemoveFromFavorite,
        activeIconName,
        inactiveIconName,
        activeImageIconName,
        inactiveImageIconName,
        ariaAnnounceItemAdded,
        ariaAnnouncedRemovedItem,
        addToFavoriteBtnText,
        removeFromFavoriteBtnText,
        useAnalytics,
        useLowerCase,
        dynamicEvents,
    } = widgetProperties;

    const locale = i18n.getLocale();

    const { data } = props;
    const theme = data?.themeSelected;

    const boundItemToAddOrRemoveFromFavorite = i18n.format(
        itemToAddOrRemoveFromFavorite || '',
        data
    );
    const boundAddToFavoriteBtnText = i18n.format(addToFavoriteBtnText || '', data);
    const boundRemoveFromFavoriteBtnText = i18n.format(removeFromFavoriteBtnText || '', data);
    const boundAriaAnnounceItemAdded = i18n.format(ariaAnnounceItemAdded || '', data);
    const boundAriaAnnouncedRemovedItem = i18n.format(ariaAnnouncedRemovedItem || '', data);

    const onTriggerTracking = (event: string) => {
        tracking({
            event: event,
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            useLowerCase: useLowerCase,
        });
    };

    return (
        <div
            css={css`
                ${!theme ? defaultStyles : themeStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles}
            `}
            className={`${locale === 'fr-CA' ? 'french' : ''} ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
        >
            <FavoriteButton
                itemToAddOrRemoveFromFavorite={boundItemToAddOrRemoveFromFavorite || ''}
                addToFavoriteBtnText={boundAddToFavoriteBtnText || ''}
                removeFromFavoriteBtnText={boundRemoveFromFavoriteBtnText || ''}
                activeIconName={activeIconName}
                inactiveIconName={inactiveIconName}
                activeImageIconName={activeImageIconName}
                inactiveImageIconName={inactiveImageIconName}
                shouldDisplayTextIconOrBoth={shouldDisplayTextIconOrBoth}
                storageName={storageName}
                ariaAnnounceItemAdded={boundAriaAnnounceItemAdded || ''}
                ariaAnnouncedRemovedItem={boundAriaAnnouncedRemovedItem || ''}
                onTriggerTracking={onTriggerTracking}
                useAnalytics={useAnalytics}
            />
        </div>
    );
};

export default FavoriteButtonWrapper;
